/**
 * Notice-text format (docs/modules/notices.md section 2): plain text with a small Markdown subset.
 * Pure functions, no HTML anywhere: renderers turn the blocks into React / React Native elements or PDF text, so no
 * sanitiser is needed. Input is untrusted: size is bounded and everything runs in (near) linear time with a hand-written
 * scanner and no backtracking regexes.
 */
/** Same value as NOTICE_BODY_MAX in the notices schema; text beyond it is ignored by the parser. */
export const NOTICE_PARSE_MAX_CHARS = 20000;
const MAX_BLOCKS = 2000;
const MAX_LIST_ITEMS = 500;
const MAX_LINK_TEXT = 300;
const MAX_LINK_URL = 2000;
function trimEnd(s) {
    let end = s.length;
    while (end > 0 && (s[end - 1] === ' ' || s[end - 1] === '\t' || s[end - 1] === ' '))
        end--;
    return end === s.length ? s : s.slice(0, end);
}
/** \r\n and \r become \n, control characters (including NUL) are removed, trailing spaces are trimmed, at most 3 blank lines in a row. */
export function normaliseNoticeBody(body) {
    const text = body.replace(/\r\n?/g, '\n').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '');
    const out = [];
    let blank = 0;
    for (const raw of text.split('\n')) {
        const line = trimEnd(raw);
        if (line === '') {
            blank++;
            if (blank > 3)
                continue;
        }
        else {
            blank = 0;
        }
        out.push(line);
    }
    return out.join('\n');
}
/** What the content hash covers: `title + "\n\n" + body`. Callers pass the normalised body. */
export function noticeContentForHash(title, body) {
    return `${title}\n\n${body}`;
}
const isSpace = (ch) => ch === undefined || ch === ' ' || ch === '\t' || ch === '\n';
/** Parses one run of text (a heading, a list item or a paragraph with its line breaks kept). */
function parseInlines(src) {
    const out = [];
    const push = (text, style, href) => {
        if (text === '')
            return;
        const last = out[out.length - 1];
        if (!href && last && !last.href && !!last.bold === style.bold && !!last.italic === style.italic) {
            last.text += text;
            return;
        }
        const inline = { text };
        if (style.bold)
            inline.bold = true;
        if (style.italic)
            inline.italic = true;
        if (href)
            inline.href = href;
        out.push(inline);
    };
    // Once a marker has no closer after a position, no later opener at the top level can find one either.
    const noCloser = { bold: Infinity, italic: Infinity };
    const findBoldClose = (s, from, to) => {
        if (from >= noCloser.bold)
            return -1;
        for (let j = s.indexOf('**', from); j !== -1 && j < to; j = s.indexOf('**', j + 1)) {
            if (j > from && !isSpace(s[j - 1]))
                return j;
        }
        if (to === s.length)
            noCloser.bold = Math.min(noCloser.bold, from);
        return -1;
    };
    const findItalicClose = (s, from, to) => {
        if (from >= noCloser.italic)
            return -1;
        for (let j = s.indexOf('*', from); j !== -1 && j < to; j = s.indexOf('*', j + 1)) {
            if (s[j + 1] === '*') {
                j++; // skip a "**" pair; the loop then continues after it
                continue;
            }
            if (j > from && !isSpace(s[j - 1]))
                return j;
        }
        if (to === s.length)
            noCloser.italic = Math.min(noCloser.italic, from);
        return -1;
    };
    /** Tries a link at `i` (s[i] === '['). Returns [text, url, nextIndex] or null. */
    const tryLink = (s, i) => {
        let j = i + 1;
        const limit = Math.min(s.length, i + 1 + MAX_LINK_TEXT);
        while (j < limit && s[j] !== ']' && s[j] !== '\n')
            j++;
        if (j >= limit || s[j] !== ']' || j === i + 1 || s[j + 1] !== '(')
            return null;
        const text = s.slice(i + 1, j);
        const urlStart = j + 2;
        const urlLimit = Math.min(s.length, urlStart + MAX_LINK_URL);
        let k = urlStart;
        while (k < urlLimit && s[k] !== ')' && !isSpace(s[k]))
            k++;
        if (k >= urlLimit || s[k] !== ')')
            return null;
        const url = s.slice(urlStart, k);
        if (!/^https?:\/\/[^\s]+$/i.test(url))
            return null;
        return [text, url, k + 1];
    };
    const run = (s, style, start, end, depth) => {
        let plainFrom = start;
        let i = start;
        while (i < end) {
            const ch = s[i];
            if (ch === '[') {
                const link = tryLink(s, i);
                if (link && link[2] <= end) {
                    push(s.slice(plainFrom, i), style);
                    push(link[0], style, link[1]);
                    i = plainFrom = link[2];
                    continue;
                }
            }
            else if (ch === '*' && depth < 2) {
                if (s[i + 1] === '*') {
                    if (!isSpace(s[i + 2]) && !style.bold) {
                        const close = findBoldClose(s, i + 2, end);
                        if (close !== -1) {
                            push(s.slice(plainFrom, i), style);
                            run(s, { ...style, bold: true }, i + 2, close, depth + 1);
                            i = plainFrom = close + 2;
                            continue;
                        }
                    }
                    i++; // a "**" that did not open anything stays literal as a pair
                }
                else if (!isSpace(s[i + 1]) && !style.italic) {
                    const close = findItalicClose(s, i + 1, end);
                    if (close !== -1) {
                        push(s.slice(plainFrom, i), style);
                        run(s, { ...style, italic: true }, i + 1, close, depth + 1);
                        i = plainFrom = close + 1;
                        continue;
                    }
                }
            }
            i++;
        }
        push(s.slice(plainFrom, end), style);
    };
    run(src, { bold: false, italic: false }, 0, src.length, 0);
    return out;
}
/** Parses a notice body into blocks. Never throws; text over the size limit is ignored. */
export function parseNoticeText(body) {
    const clipped = body.length > NOTICE_PARSE_MAX_CHARS * 2 ? body.slice(0, NOTICE_PARSE_MAX_CHARS * 2) : body;
    const lines = normaliseNoticeBody(clipped).slice(0, NOTICE_PARSE_MAX_CHARS).split('\n');
    const blocks = [];
    let para = [];
    let list = null;
    const flushPara = () => {
        if (para.length)
            blocks.push({ type: 'paragraph', inlines: parseInlines(para.join('\n')) });
        para = [];
    };
    const flushList = () => {
        if (list)
            blocks.push({ type: 'list', ordered: list.ordered, items: list.items });
        list = null;
    };
    for (const line of lines) {
        if (blocks.length >= MAX_BLOCKS)
            break;
        if (line.trim() === '') {
            flushPara();
            flushList();
            continue;
        }
        let level = 0;
        while (level < 3 && line[level] === '#')
            level++;
        if ((level === 1 || level === 2) && line[level] === ' ' && line.slice(level + 1).trim() !== '') {
            flushPara();
            flushList();
            blocks.push({ type: 'heading', level, inlines: parseInlines(line.slice(level + 1).trim()) });
            continue;
        }
        let ordered = null;
        let rest = '';
        if (line.startsWith('- ') && line.slice(2).trim() !== '') {
            ordered = false;
            rest = line.slice(2).trim();
        }
        else {
            let d = 0;
            while (d < 9 && line[d] >= '0' && line[d] <= '9')
                d++;
            if (d > 0 && line[d] === '.' && line[d + 1] === ' ' && line.slice(d + 2).trim() !== '') {
                ordered = true;
                rest = line.slice(d + 2).trim();
            }
        }
        if (ordered !== null) {
            flushPara();
            if (list && list.ordered !== ordered)
                flushList();
            list ??= { ordered, items: [] };
            if (list.items.length < MAX_LIST_ITEMS)
                list.items.push(parseInlines(rest));
        }
        else {
            flushList();
            para.push(line);
        }
    }
    flushPara();
    flushList();
    return blocks;
}
const inlineText = (inlines) => inlines.map((i) => (i.href ? `${i.text} (${i.href})` : i.text)).join('');
/** The notice as plain text with no markers (links as "text (address)"). Used for PDFs, search and previews. */
export function noticePlainText(body) {
    return parseNoticeText(body)
        .map((b) => {
        if (b.type === 'list')
            return b.items.map((it, n) => `${b.ordered ? `${n + 1}.` : '-'} ${inlineText(it)}`).join('\n');
        return inlineText(b.inlines);
    })
        .join('\n\n');
}
//# sourceMappingURL=notice-text.js.map